"use client";
import { CategoryIcon } from "@commerce-frontend/ui/base/CategoryIcon/CategoryIcon";
import { useStaticSiteConfig } from "@commerce-frontend/ui/providers/StaticSiteConfigProvider";
import { isValue } from "@commerce-shared/utility";
import type { FragmentOf } from "gql.tada";
import { useTranslations } from "next-intl";
import { memo } from "react";
import type {
	MenuItemFragment,
	SubMenuItemFragment,
} from "../Header.fragments";
import { MenuItemLink } from "./MenuItemLink";
import { MenuItemTrigger } from "./MenuItemTrigger";
import TopBar from "./TopBar";

type Props = {
	menuItem:
		| FragmentOf<typeof MenuItemFragment>
		| FragmentOf<typeof SubMenuItemFragment>;
	onSubMenuItemSelect: (item: FragmentOf<typeof SubMenuItemFragment>) => void;
	parentItem?: FragmentOf<typeof MenuItemFragment>;
	onBack: () => void;
};

export const SubMenu = memo(
	({ menuItem, onSubMenuItemSelect, onBack, parentItem }: Props) => {
		const t = useTranslations("Navigation");

		const { language } = useStaticSiteConfig();

		return (
			<>
				<TopBar onBack={onBack} />

				<div className="max-h-screen flex-1 overflow-y-scroll">
					<nav className="px-4 pt-8">
						<div className="border-b border-border pb-4">
							<div className="flex items-center gap-1">
								{menuItem.icon && (
									<CategoryIcon
										iconName={menuItem.icon}
										iconSize={"24"}
										className="bg-main"
									/>
								)}
								<strong className="text-main">{menuItem.label}</strong>
							</div>
						</div>

						<MenuItemLink
							label={t("submenuAllLink", {
								label:
									language === "de"
										? menuItem.label
										: menuItem.label.toLowerCase(),
							})}
							link={menuItem.link}
						/>

						{/* SubItems */}
						{menuItem.__typename === "MenuItem" && menuItem.subMenuItems
							? menuItem.subMenuItems
									?.filter(isValue)
									.map((subMenuItem) =>
										subMenuItem.subMenuLinks ? (
											<MenuItemTrigger
												key={subMenuItem.label}
												label={subMenuItem.label}
												trigger={() => onSubMenuItemSelect(subMenuItem)}
												icon={subMenuItem.icon}
											/>
										) : (
											<MenuItemLink
												key={subMenuItem.label}
												label={subMenuItem.label}
												link={subMenuItem.link}
												categoryIcon={subMenuItem.icon}
											/>
										),
									)
							: menuItem.__typename === "SubMenuItem" &&
								parentItem &&
								menuItem.subMenuLinks
									?.filter(isValue)
									.map((subItem) => (
										<MenuItemLink
											key={subItem.label}
											label={subItem.label}
											link={subItem.link}
										/>
									))}
					</nav>
				</div>
			</>
		);
	},
);

SubMenu.displayName = "SubMenu";
